import { Injectable, OnDestroy, Optional } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

/** One armed history entry standing in for a dismissible layer. */
interface BackStackEntry {
    id: number;
    close: () => void;
}

/**
 * The app's single owner of "what does the system Back button close?".
 *
 * DESIGN.md §Sheets locks the behaviour: with an overlay open, Back dismisses
 * the overlay and STAYS on the page — it must never navigate out from under
 * one. The live beta showed the gap goes wider than sheets: in the Org Board,
 * edit → Staff → Details answered Back by jumping all the way to the Inbasket,
 * because nothing but the router was listening.
 *
 * Mechanism (the pattern selection mode already proved): each layer arms one
 * same-URL history entry on open. A user Back pops it, we close the TOP layer
 * and the router never sees it. A layer closed by any other means (button,
 * backdrop, drag-dismiss) consumes its own entry with a silent `history.back()`
 * so the next real Back behaves normally.
 *
 * Everything registers HERE rather than each surface rolling its own listener —
 * two independent popstate handlers would both fire on a single Back and close
 * two layers at once.
 */
@Injectable({ providedIn: 'root' })
export class OverlayBackStackService implements OnDestroy {
    private readonly entries: BackStackEntry[] = [];
    private nextId = 1;

    /**
     * ONE history entry stands in for the whole stack, re-armed after each pop
     * while layers remain. Per-layer entries meant per-layer cleanup calls, and
     * those `history.back()`s raced each other and the router — on-device that
     * showed up as a single Back collapsing a dialog, the dialog under it AND
     * the board's edit mode in one press.
     */
    private armed = false;

    /** history.back() calls WE made; their popstate is not a user Back. */
    private pendingUnwinds = 0;

    /** True while a pop is being serviced — the router event it may raise is ours. */
    private handlingPop = false;
    private navigationSub?: { unsubscribe?: () => void };

    /**
     * Optional so the service stays testable standalone — and so a host that
     * has no Router (or a bare `new` in a spec) still works.
     */
    constructor(@Optional() private router: Router | null = null) {
        window.addEventListener('popstate', this.onPopState);

        // Material's own `closeOnNavigation` is switched OFF app-wide (it fired
        // before this handler and closed every layer at once), so closing the
        // stack on a real route change is now this service's job — otherwise a
        // dialog would hang over the page you navigated to.
        this.navigationSub = this.router?.events?.subscribe((event: unknown) => {
            if (event instanceof NavigationStart && !this.handlingPop) this.closeAll();
        });
    }

    public ngOnDestroy(): void {
        window.removeEventListener('popstate', this.onPopState);
        this.navigationSub?.unsubscribe?.();
    }

    /** Tear the whole stack down, innermost first. */
    private closeAll(): void {
        this.armed = false;
        while (this.entries.length) this.entries.pop()?.close();
    }

    /** True while a layer would consume the next Back. */
    public handles(): boolean {
        return this.entries.length > 0;
    }

    /**
     * Arm an entry for a layer. Returns the release function to call when the
     * layer closes on its own; calling it after a Back already closed the
     * layer is safe (and does nothing).
     */
    public push(close: () => void): () => void {
        const id = this.nextId++;
        this.entries.push({ id, close });
        this.arm();
        return () => this.release(id);
    }

    private arm(): void {
        if (this.armed) return;
        this.armed = true;
        // MERGE, never replace: Angular's Router keeps its own bookkeeping in
        // history.state (navigationId / ɵrouterPageId). A blank state object
        // drops it, the router's page ids go out of sync, and it "restores"
        // the previous URL on the NEXT navigation — every screen bounced
        // straight back to where you started (beta, build 29).
        history.pushState({ ...(history.state as object | null), ngOverlayBack: this.nextId }, '');
    }

    private markerIsOnTop(): boolean {
        return (history.state as { ngOverlayBack?: number } | null)?.ngOverlayBack !== undefined;
    }

    /** MatDialogRef-shaped: Back closes it, afterClosed releases the entry. */
    public trackDialog(ref: {
        close: () => void;
        afterClosed: () => { subscribe: (fn: () => void) => unknown };
    }): void {
        const release = this.push(() => ref.close());
        ref.afterClosed().subscribe(() => release());
    }

    /** MatBottomSheetRef-shaped: same contract, different verb. */
    public trackSheet(ref: {
        dismiss: () => void;
        afterDismissed: () => { subscribe: (fn: () => void) => unknown };
    }): void {
        const release = this.push(() => ref.dismiss());
        ref.afterDismissed().subscribe(() => release());
    }

    private readonly onPopState = (): void => {
        if (this.pendingUnwinds > 0) {
            // Our own silent unwind coming back around.
            this.pendingUnwinds--;
            return;
        }

        const top = this.entries.pop();
        if (!top) {
            // Nothing left to close — this Back belongs to the router.
            this.armed = false;
            return;
        }

        // The marker the browser just popped is gone; close exactly ONE layer.
        this.armed = false;
        this.handlingPop = true;
        try {
            top.close();

            // Layers still open below → re-arm, so the next Back closes the
            // next one instead of leaving the page.
            if (this.entries.length) this.arm();
        } finally {
            this.handlingPop = false;
        }
    };

    private release(id: number): void {
        const index = this.entries.findIndex((entry) => entry.id === id);
        if (index === -1) return; // A Back already consumed it.
        this.entries.splice(index, 1);

        // Deeper layers still need the marker.
        if (this.entries.length || !this.armed) return;
        if (!this.markerIsOnTop()) {
            this.armed = false;
            return;
        }

        this.armed = false;
        this.pendingUnwinds++;
        // DEFERRED, and re-checked at execution time. Layers that navigate as
        // they close (the mobile menu sheet picks a destination) start their
        // route change AFTER this call — unwinding blind then walks back over
        // the navigation the user just asked for, which is how every screen
        // bounced to the Inbasket in build 29. Leaving a spent entry behind is
        // harmless by comparison: the next Back simply returns to this page.
        setTimeout(() => {
            // Optional-called: test doubles for Router rarely implement it.
            if (this.router?.getCurrentNavigation?.() || !this.markerIsOnTop()) {
                this.pendingUnwinds--;
                return;
            }
            history.back();
        });
    }
}
